import React from 'react';
import { TIER_CONFIG } from '../PrizeGallery';

/**
 * HeroPrizeSpotlight Component
 * 
 * Displays a featured prize image in the hero section with theme-specific styling.
 * Designed for grand-prize spotlight approach: large featured image beside hero text.
 * 
 * @param {Object} prize - Prize object with image_url, name, tier, and optional value
 * @param {'standard' | 'style1'} theme - Visual theme variant
 * @returns {JSX.Element|null} - Rendered component or null if invalid prize
 */
export default function HeroPrizeSpotlight({ prize, theme = 'standard' }) {
  // Don't render if no prize or no image
  if (!prize || !prize.image_url) {
    return null;
  }

  const tierConfig = TIER_CONFIG[prize.tier] || TIER_CONFIG.consolation;

  // Theme-specific styling
  const themeStyles = {
    standard: {
      container: 'ring-2 ring-white/40 shadow-2xl',
      valueBg: 'bg-purple-600/90 dark:bg-purple-700/90',
      valueText: 'text-white',
    },
    style1: {
      container: 'ring-2 ring-amber-400/60 shadow-2xl drop-shadow-[0_0_15px_rgba(251,191,36,0.5)]',
      valueBg: 'bg-amber-500/90 dark:bg-amber-600/90',
      valueText: 'text-black dark:text-white',
    },
  };

  const styles = themeStyles[theme] || themeStyles.standard;

  return (
    <div className="flex flex-col gap-3">
      {/* Image Card */}
      <div className={`relative aspect-[4/3] rounded-2xl overflow-hidden ${styles.container}`}>
        {/* Tier Badge Overlay */}
        <div
          className={`absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full 
            bg-gradient-to-r ${tierConfig.gradient} 
            text-white text-xs font-bold uppercase tracking-wide shadow-lg`}
        >
          {tierConfig.label}
        </div>

        {/* Prize Image */}
        <img
          src={prize.image_url}
          alt={prize.name || 'Prize'}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          loading="lazy"
        />

        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Optional Value Callout */}
      {prize.value != null && (
        <div className={`text-center px-4 py-2 rounded-lg ${styles.valueBg}`}>
          <div className="flex items-center justify-center gap-2">
            <span className={`text-sm font-semibold uppercase tracking-wide ${styles.valueText} opacity-90`}>
              Win:
            </span>
            <span className={`text-2xl font-black ${styles.valueText}`}>
              ${prize.value.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
